<template>
    <div
        class="running-tasks"
        :class="{ open, closed: !open }"
        v-if="runningTasks && runningTasks.length > 0">
        <div class="title-row">
            <div class="title">
                Achtergrondtaken
                <template v-if="!open">
                    ({{ finishedTasks }}/{{ totalTasks }})
                </template>
            </div>
            <i
                class="toggler fas"
                @click="open = !open"
                :class="{
                    open: open,
                    closed: !open,
                    'fa-chevron-circle-down': open,
                    ' fa-chevron-circle-up': !open,
                }" />
        </div>
        <template v-if="open">
            <div
                class="running-task"
                v-for="task in runningTasks"
                :key="'task.' + task.guid">
                <ui5-popover :id="'tooltip.task.' + task.guid">
                    <div class="top-actions">
                        <i
                            class="fa fa-clipboard"
                            title="Kopieer GUID naar klembord"
                            @click="copyTextToClipboard(task.guid)" />
                        <i
                            class="fa fa-times"
                            @click="
                                $el.ownerDocument
                                    .getElementById('tooltip.task.' + task.guid)
                                    .close()
                            " />
                    </div>
                    <div v-if="task.error" class="error">
                        {{ task.error }}
                    </div>
                    <div class="warning" v-else-if="task.cancelled">
                        Geannuleerd
                    </div>
                    <template v-else-if="task.finished">
                        <div class="success" v-if="getValue(task) >= 100">
                            Succesvol uitgevoerd
                        </div>
                        <div class="warning" v-else>Voortgang geëindigd</div>
                    </template>
                    <div v-else class="running">Bezig...</div>
                    <div>
                        <template
                            v-if="
                                task.finished || task.error || task.cancelled
                            ">
                            Duur: {{ getActualTime(task) }}
                        </template>
                        <template v-else>
                            Geschatte resterende duur:
                            {{ getEstimatedRemainingTime(task) }}
                        </template>
                    </div>
                </ui5-popover>
                <ui5-progress-indicator
                    :value="getValue(task)"
                    :value-state="getValueState(task)"
                    @click="
                        $el.ownerDocument
                            .getElementById('tooltip.task.' + task.guid)
                            .showAt($event.target)
                    "
                    :class="{ error: task.error }"
                    :display-value="getDisplayValue(task)" />
                <i
                    v-if="!task.finished && !task.cancelled"
                    @click="cancelTask(task)"
                    :class="{
                        'fa-ban': !task.cancelling,
                        'fa-spinner': task.cancelling,
                    }"
                    class="fa fa-ban" />
                <i @click="removeTask(task)" class="fa fa-times" />
            </div>
        </template>
    </div>
</template>
<script>
import runningTasksApi from '../api/runningTasks';
import { secondsToHoursMinutesSeconds } from '../helpers/DateHelper';
import { copyTextToClipboard } from '../helpers/GeneralHelper';
import { showConfirmDialog, showInfoMessage } from '../dialogs';

let interval = null;

export default {
    data() {
        return {
            open: true,
        };
    },
    computed: {
        finishedTasks() {
            return (this.runningTasks || []).filter(
                (x) => x.error || this.getValue(x) >= 100
            ).length;
        },
        totalTasks() {
            return this.runningTasks ? this.runningTasks.length : 0;
        },
        runningTasks: {
            get() {
                return this.$store.getters['general/getRunningTasks'];
            },
            set(newValue) {
                this.$store.commit('general/setRunningTasks', [...newValue], {
                    root: true,
                });
            },
        },
    },
    methods: {
        copyTextToClipboard(text) {
            copyTextToClipboard(text).then(() => {
                showInfoMessage(
                    'De GUID van de taak werd naar het klembord gekopieerd'
                );
            });
        },
        getActualTime(task) {
            const startTime = new Date(task.startTime);
            const endTime =
                task.endTime instanceof Date
                    ? task.endTime
                    : new Date(task.endTime);
            return this.getTimeDiff(startTime, endTime);
        },
        getEstimatedRemainingTime(task) {
            const startTime = new Date(task.startTime);
            const spentSeconds = parseInt(
                new Date().getTime() / 100 - startTime.getTime() / 100
            );
            const totalSeconds = parseInt(
                (spentSeconds / task.currentProgress) * task.targetProgress
            );
            const remainingSeconds = totalSeconds - spentSeconds;
            return secondsToHoursMinutesSeconds(remainingSeconds);
        },
        getTimeDiff(startTime, endTime) {
            const startSecs = startTime.getTime() / 1000;
            const endSecs = endTime.getTime() / 1000;
            return secondsToHoursMinutesSeconds(parseInt(endSecs - startSecs));
        },
        cancelTask(task) {
            showConfirmDialog({
                title: 'Taak afbreken',
                content: 'Bent u zeker dat u deze taak wilt afbreken?',
                confirm: () => {
                    task.cancelling = true;
                    runningTasksApi.cancel(task.guid);
                },
                closeOnCancel: true,
                closeOnOk: true,
                confirmButton: 'Ja',
                cancelButton: 'Nee',
            });
        },
        removeTask(task) {
            const remover = () => {
                this.runningTasks = this.runningTasks.filter(
                    (x) => x.guid != task.guid
                );
                runningTasksApi.removeFromCache(task.guid);
            };

            if (task.finished) {
                remover();
            } else {
                showConfirmDialog({
                    title: 'Taak verwijderen',
                    content:
                        'De taak is nog niet afgesloten. Bent u zeker dat u ze wilt verwijderen?',
                    confirm: remover,
                    closeOnCancel: true,
                    closeOnOk: true,
                    confirmButton: 'Ja',
                    cancelButton: 'Nee',
                });
            }
        },
        getValue(task) {
            if (task.error || task.cancelled) {
                return 100;
            }
            return (task.currentProgress / task.targetProgress) * 100;
        },
        getDisplayValue(task) {
            const progress = task.currentProgress + '/' + task.targetProgress;
            let value = progress;
            if (task.error) {
                value = 'mislukt' + ' (' + progress + ')';
            }
            if (task.cancelled) {
                value = 'onderbroken' + ' (' + progress + ')';
            }
            return task.label + ': ' + value;
        },
        getValueState(task) {
            const value = this.getValue(task);
            if (task.error) {
                return 'Error';
            }
            if (task.cancelled) {
                return 'Warning';
            }
            if (value < 100) {
                if (task.finished) {
                    return 'None';
                }
                return 'Information';
            }
            return 'Success';
        },
        fetchTasks() {
            const guids = (this.runningTasks || [])
                .filter((x) => !x.finished && !x.cancelled)
                .map((x) => x.guid);
            if (guids.length) {
                runningTasksApi.poll(guids).then((response) => {
                    const newTasks = response.data.data;
                    newTasks.forEach((newTask) => {
                        const oldTaskIndex = this.runningTasks.findIndex(
                            (x) => x.guid == newTask.guid
                        );
                        if (oldTaskIndex == -1) {
                            this.runningTasks.push(newTask);
                        } else {
                            this.runningTasks[oldTaskIndex] = newTask;
                        }
                    });

                    this.runningTasks.forEach((oldTask) => {
                        if (
                            newTasks.findIndex(
                                (newTask) => newTask.guid == oldTask.guid
                            ) == -1
                        ) {
                            oldTask.finished = true;
                            oldTask.endTime = new Date();
                        }
                    });

                    this.runningTasks = [...this.runningTasks];
                });
            }
        },
    },
    mounted() {
        this.fetchTasks();
        interval = window.setInterval(() => {
            this.fetchTasks();
        }, 5000);
    },
    beforeDestroy() {
        window.clearInterval(interval);
    },
};
</script>
